import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  NgZone,
  OnInit,
  QueryList,
  signal,
  Type,
  ViewChild,
  ViewChildren,
} from '@angular/core';

import { EntryService, MobileView } from '../../entry.service';
import { catchError, combineLatest, filter, map, of, take, tap } from 'rxjs';
import { ModelConfidenceComponent } from '../model-confidence/model-confidence.component';
import { LinearLegendsComponent } from '../../linear-legends/linear-legends.component';
import { DomainsComponent } from '../domains/domains.component';
import { MacromoleculesComponent } from '../macromolecules/macromolecules.component';
import { AnnotationsComponent } from '../annotations/annotations.component';
import { SimilarProteinsComponent } from '../similar-proteins/similar-proteins.component';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { Router } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { EntryData, Structure, TedDomain } from '../../structure-data.models';
import { EntrySelectors } from 'src/app/state/entry.selectors';
import { Store } from '@ngrx/store';
import { CommonService } from 'src/app/common.service';
import { GoogleAnalyticsService } from 'src/app/google-analytics.service';
import { ConfigService } from 'src/app/config.service';
import { PlatformService } from 'src/app/platform.service';
import { MolstarPluginService } from 'src/app/molstar-plugin-service';
import {
  applyAlphaMissenseTooltips,
  colorByAlphaMissense,
  resetColoring,
  resetTooltips
} from '../../../molstar-alpha-missense';
import { colorByTed } from '../../../molstar-alpha-ted';
import { UtilService } from 'src/app/util.service';
import { MappedTedDomain } from '../../domains-tab/domains-tab.component';

import { PaeSelectedRegions } from 'src/app/pae-view/pae-view.component';

import { PaeDataService, Chain} from 'src/app/pae-view/pae-data.service';
export enum MobileTabChips {
  MConfidence = 'MConfidence',
  Macromolecules = 'Macromolecules',
  Domains = 'Domains',
  Annotations = 'Annotations',
  SProteins = 'SProteins',
}
declare var PDBeMolstarPlugin: any;

type ColorScheme = 'plddt' | 'missense' | 'ted';

const colorScheme = {
  PLDDT: 'plddt',
  MISSENSE: 'missense',
  TED: 'ted'
}

@Component({
  selector: 'molstar-mobile',
  templateUrl: './molstar.component.html',
  styleUrl: './molstar.component.css',
  standalone: false,
})
export class MolstarComponent {
  public afdb_accession: string;
  private entryService = inject(EntryService);
  private bottomSheet = inject(MatBottomSheet);
  private readonly zone = inject(NgZone);
  private router = inject(Router);
  public hydrationReady: boolean = false;

  public activeDomain = signal<MappedTedDomain>(null);
  public mappedTedDomainData = signal<MappedTedDomain[]>([]);
  public tedDomainNumberForClickActive = signal<number | null>(null);
  public uniprotData = signal<any>({});

  public pdbeMolstar: any;
  public structure: Structure;
  public lastUpdateDate: string;
  public molLoadComplete: boolean = false;
  isColorby: boolean= true;

  constructor(
    private store: Store,
    public commonService: CommonService,
    public gaService: GoogleAnalyticsService,
    private configService: ConfigService,
    private utilService: UtilService,
    private platform: PlatformService,
    private el: ElementRef,
    private molstarPluginService: MolstarPluginService,
    private paeDataService: PaeDataService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}
  public  superposeData = [];
  accession = toSignal(this.store.select(EntrySelectors.accession));
  public entryData = signal<EntryData>({} as EntryData);
  public tedDomainData = signal<TedDomain[]>([]);
  public csvAmData = signal<string>('');
  private readonly destroyRef = inject(DestroyRef);
  public isAlphaMis: boolean;
  public isModalConf: boolean;
  public isOldUniprotEntry: boolean = false;

  @ViewChild('molstarContainer') molstarContainer!: ElementRef;
  @ViewChildren('chipEl') chipElements!: QueryList<ElementRef<HTMLElement>>;

  public get mobileTabChips(): any[] {
    return [
      { label: 'Model Confidence', id: MobileTabChips.MConfidence },
      ...(this.entryData().isComplex ? [{ label: 'Macromolecules', id: MobileTabChips.Macromolecules }] : []),
      ...(!this.entryData().isComplex ? [{ label: 'Domains', id: MobileTabChips.Domains }] : []),
      ...(!this.entryData().isComplex ? [{ label: 'Annotations', id: MobileTabChips.Annotations }] : []),
      ...(!this.entryData().isComplex ? [{ label: 'Similar Proteins', id: MobileTabChips.SProteins }] : []),
    ];
  }
  ngOnInit(): void {
   
    combineLatest([
      this.store.select(EntrySelectors.entryData).pipe(filter(Boolean)),
      this.store.select(EntrySelectors.tedDomainData).pipe(filter(Boolean)),
      this.store.select(EntrySelectors.csvAmData).pipe(),
      this.store.select(EntrySelectors.uniprotData).pipe(),
    ])
      .pipe(
        map(([entryData, tedDomainData, csvAmData, uniprotData]) => {
          this.entryData.set(entryData)
          this.tedDomainData.set(tedDomainData)
          this.csvAmData.set(csvAmData)
          this.uniprotData.set(uniprotData)
          this.afdb_accession = this.entryData().modelEntityId;

          const sequenceVersionDate = this.entryData()?.sequenceVersionDate;
          const uniprotSequenceModified = this.uniprotData()?.sequence?.modified;
          if (sequenceVersionDate && uniprotSequenceModified) {
            this.isOldUniprotEntry = this.utilService.areDatesDifferent(sequenceVersionDate, uniprotSequenceModified);
          }

        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    if (this.platform.isBrowserPlatform()) {
      this.hydrationReady = true;
    }
    this.initMolstar();
    this.processTedDomainsTrack();
    if(this.selectedTabName() === 'MConfidence'){
      this.onTabClick({ label: "Model Confidence", id: MobileTabChips.MConfidence }, false)
    }
    if(this.selectedTabName() === 'Macromolecules'){
      this.onTabClick({ label: 'Macromolecules', id: MobileTabChips.Macromolecules }, false)
    }
    if(this.selectedTabName() === 'SProteins'){
      this.onTabClick({ label: "Similar Proteins", id: MobileTabChips.SProteins }, false)
    }
  }
  setsuperpose_color(){
    if (!this.pdbeMolstar) {
      return;
    }

    if(this.isColorby){
      this.pdbeMolstar.visual.clearSelection('main');
    }else{
      this.pdbeMolstar.visual.select({ data: [{ color: '#A9ABAA' }], structureId: 'main' });
    }
  }

  public processTedDomainsTrack(){
    if (this.tedDomainData()) {
      const tedDomainData = [];
      const seqLength = this.entryData().sequenceEnd;
      this.tedDomainData().forEach((item, index) => {
        for (let seg of item.segments) {
          tedDomainData.push({
            start: seg.af_start,
            end: seg.af_end,
            label: item.cath_label,
            qscore: item.qscore,
            cath_assignment_level: item.cath_assignment_level,
            tedNumber: item.ted_domain_no,
            plddt: item.plddt,
            quality: this.getQuality(item.qscore, item.cath_assignment_level),
            color: this.utilService.fetchColorByIndex(index),
            // width: this.getResWidth(seg.af_start, seg.af_end, this.lengthOfProtein(), 100 ),
            // width: (seg.af_end-seg.af_start+1) / this.lengthOfProtein()* 320 ,
            left: (seg.af_start-1) / seqLength* 100 ,
            right: (seqLength-seg.af_end) / seqLength* 100 ,
          });
        }
      });
      const sortedTedDomainData = tedDomainData.sort((a, b) => {
        if (a.start === b.start) {
          return a.end - b.end;
        }
        return a.start - b.start;
      });
      const mappedTedDomainDataWithGap = sortedTedDomainData.map(
        (item, index) => {
          return {
            ...item,
            gap:
              index !== sortedTedDomainData.length - 1
                ? ((sortedTedDomainData[index + 1].start -
                    sortedTedDomainData[index].end) /
                    seqLength) *
                  262
                : 0,
          };
        }
      );
      
      this.mappedTedDomainData.update(() => mappedTedDomainDataWithGap);
      this.activeDomain.set(this.mappedTedDomainData()[0]);
      const similarTeds = this.mappedTedDomainData().filter(
        (item) => item.tedNumber === this.activeDomain().tedNumber
      );
      const boundaries = this.getBoundaries(similarTeds);
      const length = this.getTotalLength(similarTeds);
      this.activeDomain.set({ ...this.mappedTedDomainData()[0], boundaries, length });
      this.tedDomainNumberForClickActive.set(this.activeDomain().tedNumber);
      this.entryService.setActiveDomain(this.activeDomain())
    }
  }

  public async handleSelectedCard(domain){
    this.applyMolstarColoring('ted');
    this.utilService.clickDomainTrack('active');
    const similarTeds = this.mappedTedDomainData().filter(
      (item) => item.tedNumber === domain.tedNumber
    );
    const boundaries = this.getBoundaries(similarTeds);
    const length = this.getTotalLength(similarTeds);
    const quality = this.getQuality(domain.qscore, domain.cath_assignment_level);
    const qscore = domain.qscore;
    const cath_assignment_level = domain.cath_assignment_level;
    const data = [
      ...similarTeds.map((domain) => ({
        struct_asym_id: 'A',
        start_residue_number: domain.start,
        end_residue_number: domain.end,
        focus: true,
        color: domain.color,
      })),
    ];
    await this.pdbeMolstar.visual.focus(data);

    this.activeDomain.set({ ...domain, boundaries, length, qscore, cath_assignment_level, quality });
    
    this.tedDomainNumberForClickActive.set(this.activeDomain().tedNumber);
    this.changeDetectorRef?.detectChanges();
  }

  public getQuality(qscore: number, assignment: string): string {
    if (qscore >= 75 && assignment.includes("C.A.T.H. Homologous Superfamily")) {
        return "High-confidence"; //2 stars
    } 
    if (qscore >= 70 && (assignment.includes("C.A.T.H. Homologous") || assignment.includes("C.A.T. Fold"))) {
        return "Moderate"; //1 star
    }
    return "Uncertain"; // No star/Empty star
  }

  private getBoundaries(similarTeds): string {
    const mappedSimilarTeds = similarTeds.map(
      (item) => item.start + '-' + item.end
    );
    return mappedSimilarTeds.join(', ');
  }

  private getTotalLength(similarTeds) {
    return similarTeds.reduce(
      (acc, current) => acc + (current.end - current.start) + 1,
      0
    );
  }

  private selectedComponent = this.entryService.selectedComponent;
  public selectedTabName = this.entryService.selectedTabName;

  public navigateBackToOverview() {
    this.bottomSheet.dismiss();
    this.entryService.setMobileView(MobileView.Overview);
  }
  applyMolstarColoring(kind: string) {
    if (!this.pdbeMolstar) {
      return;
    }

    if (kind === 'missense') {
      this.isAlphaMis = true;
      this.isModalConf = false;
      // colorByAlphaMissense(this.pdbeMolstar, this.csvAmData);
      
      colorByAlphaMissense(this.pdbeMolstar, this.csvAmData());
      
      applyAlphaMissenseTooltips(this.pdbeMolstar, this.csvAmData());
      this.gaService.eventEmitter(
        'AM_toggle',
        'AlphaMissense',
        'click',
        'AM_toggle',
        'Toggle between AM and pLDDT colouring on Mol*'
      );
    } else if (kind === 'plddt') {
      this.isAlphaMis = false;
      this.isModalConf = true;
      resetColoring(this.pdbeMolstar);
      resetTooltips(this.pdbeMolstar);
    } else if (kind === 'ted') {
      // this.setUpTEDToggleUI();
      colorByTed(this.pdbeMolstar, this.tedDomainData());
      this.gaService.eventEmitter(
        'domains_toggle',
        'domains',
        'click',
        'domains_toggle',
        'Toggle between TED colouring on Mol*'
      );
    }  else {
      console.error('Unknow coloring kind:', kind);
    }
  }

   public onTabClick(chip: { label: string; id: string }, isManual: boolean): void {
  
    let tag = '';
    let msg = '';
    let componentToOpen: Type<any> | null = null;
    const selectedTabId = chip.id;
    this.entryService.updateSelectedTabName(selectedTabId);
    // scrolls into view horizontally on mobile without anti pattern
    this.zone.onStable.pipe(take(1)).subscribe(() => {
      const chipElement = this.chipElements.find(
        (el) => el.nativeElement.dataset['id'] === chip.id
      );
      chipElement?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    });
   
    if(selectedTabId === "Annotations" && this.csvAmData()){
      this.hideAllSuperposition();
      this.applyMolstarColoring('missense');
    }
    if(selectedTabId === "Annotations" && !this.csvAmData()){
      this.hideAllSuperposition();
      this.applyMolstarColoring('plddt');
    }
    if(selectedTabId === "MConfidence"){
      this.hideAllSuperposition();
      this.applyMolstarColoring('plddt');
    }
    if(selectedTabId === "Macromolecules"){
      this.hideAllSuperposition();
      this.applyMolstarColoring('plddt');
    }
    if(selectedTabId === "Domains"){
      this.hideAllSuperposition();
      this.applyMolstarColoring('ted');
    }
    if(selectedTabId === "SProteins"){
      this.applyMolstarColoring('plddt');
    }
    switch (selectedTabId) {
      case MobileTabChips.MConfidence:
        this.entryService.updateSelectedComponent(ModelConfidenceComponent);
        componentToOpen = ModelConfidenceComponent;
        tag = 'model_confidence_mobile';
        msg = 'Tap on "Model Confidence" tab in mobile';
        break;
      case MobileTabChips.Macromolecules:
        this.entryService.updateSelectedComponent(MacromoleculesComponent);
        componentToOpen = MacromoleculesComponent;
        tag = 'macromolecules_mobile';
        msg = 'Tap on "Macromolecules" tab in mobile';
        break;
      case MobileTabChips.Domains:
        this.entryService.updateSelectedComponent(DomainsComponent);
        componentToOpen = DomainsComponent;
        tag = 'domains_mobile';
        msg = 'Tap on "Domains" tab in mobile';
        break;
      case MobileTabChips.Annotations:
        this.entryService.updateSelectedComponent(AnnotationsComponent);
        componentToOpen = AnnotationsComponent;
        tag = 'annotations_mobile';
        msg = 'Tap on "Annotations" tab in mobile';
        break;
      case MobileTabChips.SProteins:
        this.entryService.updateSelectedComponent(SimilarProteinsComponent);
        componentToOpen = SimilarProteinsComponent;
        tag = 'similar_proteins_mobile';
        msg = 'Tap on "Similar proteins" tab in mobile';
        break;
      default:
        this.entryService.updateSelectedComponent(null);
        componentToOpen = null;
        break;
    }
    this.gaService.eventEmitter('mobile3D_tab_navigation', 'mobile3D_navigation', 'tap', tag, msg);

    if (componentToOpen !== null) {
      const componentInstance = componentToOpen as Type<any>;
      if(selectedTabId === 'MConfidence'){
        // this.initMolstar()
        this.bottomSheet.open(componentInstance, {
          height: '12%',
          hasBackdrop: false,
          panelClass: 'custom-bottom-sheet',
        });
      }
      if(selectedTabId === 'Macromolecules'){
        const refMacromolecules = this.bottomSheet.open(componentInstance, {
          height: '12%',
          hasBackdrop: false,
          panelClass: 'custom-bottom-sheet',
        });
        refMacromolecules.instance.resetToListState();
        refMacromolecules.instance.handleSelectedMacromoleculeParent.subscribe(async (data) => {
          await this.handleSelectedMacromolecule(data);
        });
      }
      if(selectedTabId === 'Domains'){
        // this.initMolstar()
        const refDomains =  this.bottomSheet.open(componentInstance, {
          height: '12%',
          hasBackdrop: false,
          panelClass: 'custom-bottom-sheet',
        });
        refDomains.instance.handleSelectedDomainParent.subscribe(async (data) => {
          await this.handleSelectedCard(data);
        });
      }
      if(selectedTabId === 'Annotations'){
        // this.initMolstar()
        this.bottomSheet.open(componentInstance, {
          height: '12%',
          hasBackdrop: false,
          panelClass: 'custom-bottom-sheet',
        });
      }
      if(selectedTabId === 'SProteins'){
        if(this.superposeData.length > 0){
          this.showSelectedTargets();
        }
        const ref = this.bottomSheet.open(SimilarProteinsComponent, {
          height: '12%',
          hasBackdrop: false,
          panelClass: 'custom-bottom-sheet'
        });
         ref.instance.handleSelectedStructureinParent.subscribe(async (data) => {
          await this.loadTarget(data.accession, data.apidata, data.color, data.checked);
        });
      }
    } else {
      this.bottomSheet.dismiss();
    }
  }
  async showSelectedTargets(){
    for (const entry of this.superposeData) {
      this.pdbeMolstar.visual.structureVisibility(entry.accession, true);
    }
  }
  async loadTarget(id, apidata, color, checked){
    this.superposeData.push({accession: id, targetToShow: apidata.targetToShow, refLinkId: apidata.refLinkId, rmsd:`RMSD `,  desc:apidata.desc, color:color, isShow: true, apidata:apidata});
     
    this.changeDetectorRef?.detectChanges();
    if(checked){
      const result = await PDBeMolstarPlugin.extensions.Foldseek.loadFoldseekSuperposition(this.pdbeMolstar, id, apidata, color);
    }
    if(!checked ){
      await this.pdbeMolstar.deleteStructure(id).then(() => {});
    }
  }
  hideAllSuperposition() {
    if (!this.pdbeMolstar) {
      return;
    }

    for (const entry of this.superposeData) {
      this.pdbeMolstar.visual.structureVisibility(entry.accession, false);
      // this.pdbeMolstar.deleteStructure(entry.accession).then(() => {});
    }
    // this.superposeData = [];
    this.changeDetectorRef?.detectChanges();
  }

  initMolstar(){

    this.structure = {
      model_url: this.entryData().bcifUrl,
      model_format: 'cif',
      binary: true,
    };
    setTimeout(() => {
      this.handleMolstar(this.structure);
    }, 10);

  }

  async handleMolstar(structure: Structure): Promise<void> {
    let options = {};

    // if(this.selectedTabName() !== 'SProteins'){
    //    options = {
    //     customData: {
    //       url: structure.model_url,
    //       format: structure.model_format,
    //       binary: structure.binary,
    //     },
    //     subscribeEvents: false,
    //     bgColor: { r: 255, g: 255, b: 255 },
    //     selectInteraction: false,
    //     alphafoldView: true,
    //     reactive: true,
    //     sequencePanel: true,
    //     hideCanvasControls: ['animation'],
    //     landscape: true,
    //     hideControls: true,
    //     validationAnnotation: true,
    //     granularity: 'residue'
        
    //   };
    // }else{

       options = {
        customData: {
          url: structure.model_url,
          format: structure.model_format,
          binary: structure.binary,
        },
        bgColor: { r: 255, g: 255, b: 255 },
        alphafoldView: true,
        sequencePanel: true,
        hideControls: true,
        hideCanvasControls: [ 'animation'],
        visualStyle: {
            polymer: { type: 'putty', size: 'uniform' },
            het: 'ball-and-stick',
            nonStandard: 'ball-and-stick',
            carbs: 'carbohydrate',
        },
        hideStructure: ['water'],
      };
    // }
   
    if (this.pdbeMolstar) {
      this.pdbeMolstar.visual.update(options, true);
    } else {
      const ele = this.el.nativeElement.querySelector('.mobile-molstar-container');
      
      if (ele) {
        // this.pdbeMolstar = new PDBeMolstarPlugin();
        await this.molstarPluginService.loadPlugin();
        const pluginInstance = this.molstarPluginService.createInstance();
        this.pdbeMolstar = pluginInstance;
        const layout = [
          {target: ele, component:this.molstarPluginService.getClass().UIComponents.FullLayoutNoControlsUnlessExpanded },
        ]
        this.pdbeMolstar.render(layout, options);
        // this.pdbeMolstar.render(ele, options);

        // Update Mol load complete flag
        this.pdbeMolstar.events.loadComplete.subscribe((e) => {
          this.molLoadComplete = true;
        });
      }
    }

   
  }

  async clearSeqSelection(): Promise<void> {
    // const selectedSeqElements = this.el.nativeElement.querySelectorAll(
    //   '.ms-pae-selection, .ms-pae-select-green, .ms-pae-select-orange, .ms-pae-select-yellow'
    // );
    // if (selectedSeqElements) {
    //   selectedSeqElements.forEach((selEle) => {
    //     selEle.classList.remove('ms-pae-selection');
    //     selEle.classList.remove('ms-pae-select-green');
    //     selEle.classList.remove('ms-pae-select-orange');
    //     selEle.classList.remove('ms-pae-select-yellow');
    //   });
    // }
    await this.pdbeMolstar.visual.clearSequenceColor();
  }

  private async handleSelectedMacromolecule(data: { chainId: string; otherChainIds: string[] }): Promise<void> {
    if (!this.pdbeMolstar || !data?.chainId) {
      return;
    }

    await this.pdbeMolstar.visual.select({
      data: data.otherChainIds.map((id) => ({ auth_asym_id: id, color: null, opacity: 0.4 })),
    });

    await this.pdbeMolstar.visual.focus([
      {
        auth_asym_id: data.chainId,
      },
    ]);
  }

  // applySeqSelection(selectionData) {
  //   this.clearSeqSelection();
  //   selectionData.forEach((ele) => {
  //     for (
  //       let seqid = ele.start_residue_number;
  //       seqid <= ele.end_residue_number;
  //       seqid++
  //     ) {
  //       const seqElement = this.el.nativeElement.querySelector(
  //         `.msp-sequence-present[data-seqid="${seqid}"`
  //       );
  //       if (seqElement) {
  //         seqElement.classList.add(`${ele.class}`);
  //       }
  //     }
  //   });
  // }

  validateBlankSelection(regions) {
    regions.forEach((select) => {
      if (select.start === 0 && select.end === 0) {
        this.clearSeqSelection();
        this.pdbeMolstar.visual.reset({ camera: true });
      }
    });
    return;
  }

  cmSelectionUpdate(cmSelectedRegions: PaeSelectedRegions): void {
    this.handleCmUpdate(cmSelectedRegions);
  }

  async handleCmUpdate(regions: PaeSelectedRegions): Promise<void> {
    if (!this.molLoadComplete) {
      return;
    }

    if (!regions) {
      this.clearSeqSelection();
      let colorschemValue = colorScheme.PLDDT;
      if (this.selectedTabName() === "Annotations" && this.csvAmData())
       {colorschemValue = colorScheme.MISSENSE;} 
      if (this.selectedTabName() === "Annotations" && !this.csvAmData())
       {colorschemValue = colorScheme.PLDDT; } 
      if (this.selectedTabName() === "Macromolecules")
        {colorschemValue = colorScheme.PLDDT;}

      if (this.selectedTabName() === "Domains")
        {colorschemValue = colorScheme.TED;}
      await this.applyMolstarColoring(colorschemValue);

      this.pdbeMolstar.visual.reset({ camera: true });
      return;
    }

    const proteinChains = this.paeDataService.chains();
    const selectionChains: Chain[] = proteinChains.length > 0
      ? proteinChains
      : [{
          name: 'ProteinA',
          label_asym_id: undefined,
          length: this.entryData().sequenceEnd,
          sequenceEnd: this.entryData().sequenceEnd,
          sequenceStart: 1,
        }];
    const selectionData = this.utilService.getSelectionData(regions, selectionChains);



    await this.pdbeMolstar.visual.select({
      data: selectionData,
      nonSelectedColor: '#707372',
      keepRepresentations: true,
    });
    await this.pdbeMolstar.visual.sequenceColor({
      data: selectionData,
    });

  }

  ngOnDestroy() {
    this.pdbeMolstar = undefined;
  }
}
